/**
 * Phase 16 Plan 16-03 — LinkMachinesDialog
 *
 * Multi-select dialog for adding machines to a Work Order.
 * Shows only site-allocated machines that are NOT already actively linked to the WO.
 * Submit calls useAddMachinesMutation.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useAddMachinesMutation } from '@/hooks/useWorkOrderQueries';
import { ApiError } from '@/lib/api/error';
import { toast } from '@/lib/toast';
import { formatMachineName } from '@/lib/utils/machine-label';
import { listMachinesRequest } from '@/services/machines';
import type { MachineListItem } from '@/types/machines';
import type { PaginatedResult } from '@/types/roles';
import { isActiveSiteLink, type WorkOrderDetail } from '@/types/work-orders';

export interface LinkMachinesDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (o: boolean) => void;
  readonly workOrder: WorkOrderDetail;
}

export function LinkMachinesDialog({
  open,
  onOpenChange,
  workOrder,
}: LinkMachinesDialogProps): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mutation = useAddMachinesMutation();

  // BF: scope the picker to the WO's ACTIVE sites.
  //
  // This used to read `workOrder.siteId` — the legacy single-site field dropped from the API by
  // Phase 22 D-228. It is therefore always undefined, so every call fell through to the
  // unscoped branch and listed every active machine in the system. Picking one from a site that
  // is not on this WO then failed server-side with "machines not allocated to any of the Work
  // Order sites", which reads as a broken button rather than a filtered list.
  //
  // Mirrors WorkOrderCreateWizard step 3: single site → let the API filter; multiple sites →
  // fetch active machines once and narrow client-side (the endpoint takes a single siteId).
  const activeSites = useMemo(
    () => (workOrder.sites ?? []).filter(isActiveSiteLink),
    [workOrder.sites],
  );
  const woSiteIds = useMemo(() => activeSites.map((s) => s.siteId), [activeSites]);
  const siteLabel = activeSites.length === 0
    ? null
    : activeSites.map((s) => s.siteName).join(', ');

  const machinesQuery = useQuery<PaginatedResult<MachineListItem>, ApiError>({
    queryKey: ['machines', 'active', 'forLinkDialog', woSiteIds],
    queryFn: async () => {
      const soleSiteId = woSiteIds.length === 1 ? woSiteIds[0] : undefined;
      if (soleSiteId !== undefined) {
        return listMachinesRequest({ siteId: soleSiteId, status: 'active', pageSize: 200 });
      }
      const all = await listMachinesRequest({ status: 'active', pageSize: 200 });
      const siteSet = new Set(woSiteIds);
      return {
        ...all,
        items: all.items.filter((m) => m.siteId !== null && siteSet.has(m.siteId)),
      };
    },
    // No sites linked yet → nothing can legally be added; skip the request entirely.
    enabled: open && woSiteIds.length > 0,
    staleTime: 30_000,
  });

  // Filter out already-linked machines
  const linkedMachineIds = useMemo(
    () => new Set(workOrder.machines.map((m) => m.machineId)),
    [workOrder.machines],
  );

  const availableMachines = useMemo(
    () => (machinesQuery.data?.items ?? []).filter((m) => !linkedMachineIds.has(m.id)),
    [machinesQuery.data, linkedMachineIds],
  );

  function toggleMachine(id: number): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleClose(): void {
    setSelectedIds(new Set());
    setErrorMessage(null);
    mutation.reset();
    onOpenChange(false);
  }

  function handleSubmit(): void {
    if (selectedIds.size === 0) return;
    setErrorMessage(null);
    mutation.mutate(
      { id: workOrder.id, body: { machineIds: [...selectedIds] } },
      {
        onSuccess: () => {
          toast.success(`${String(selectedIds.size)} machine(s) linked to ${workOrder.woNumber}`);
          setSelectedIds(new Set());
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = err.detail ?? err.message;
          setErrorMessage(msg);
          toast.error('Link failed', { description: msg });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="link-machines-dialog">
        <DialogHeader>
          <DialogTitle>Link Machines</DialogTitle>
          <DialogDescription>
            {/* BF: was workOrder.siteName — a legacy single-site field the API no longer sends,
                so this rendered "Select machines from  to link to …" with an empty name. */}
            {siteLabel === null ? (
              <>Link machines to <strong>{workOrder.woNumber}</strong>.</>
            ) : (
              <>
                Select machines from <strong>{siteLabel}</strong> to link to{' '}
                <strong>{workOrder.woNumber}</strong>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-64 overflow-y-auto">
          {woSiteIds.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="link-machines-no-sites">
              This Work Order has no sites linked yet. Add a site first — machines must be
              allocated to one of the Work Order&apos;s sites.
            </p>
          )}
          {machinesQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="link-machines-loading">
              Loading machines…
            </p>
          )}
          {machinesQuery.isError && (
            <p className="text-sm text-destructive" data-testid="link-machines-error">
              Failed to load machines.
            </p>
          )}
          {availableMachines.length === 0 && machinesQuery.isSuccess && (
            <p className="text-sm text-muted-foreground" data-testid="link-machines-empty">
              No available machines to link.
            </p>
          )}
          {availableMachines.map((m) => (
            <div key={m.id} className="flex items-center gap-3">
              <Checkbox
                id={`link-machine-${String(m.id)}`}
                checked={selectedIds.has(m.id)}
                onCheckedChange={() => { toggleMachine(m.id); }}
                data-testid={`link-machine-checkbox-${String(m.id)}`}
              />
              <Label htmlFor={`link-machine-${String(m.id)}`} className="font-normal cursor-pointer">
                {formatMachineName(m.name, m.vehicleNumber, m.code)}
                <span className="ml-2 text-xs text-muted-foreground">({m.machineTypeName})</span>
              </Label>
            </div>
          ))}
          {errorMessage !== null && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="link-machines-dialog-error"
            >
              {errorMessage}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
            data-testid="link-machines-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={mutation.isPending || selectedIds.size === 0}
            data-testid="link-machines-dialog-submit"
          >
            {mutation.isPending
              ? 'Linking…'
              : `Link ${String(selectedIds.size)} machine${selectedIds.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
